import { create } from 'zustand'
import { generateCSSVariables, type ThemeName } from '@/utils/theme'

export type ThemeType = ThemeName
export type ThemeMode = 'system' | ThemeType
export type FontOption = 'song' | 'system'

interface ThemeState {
  theme: ThemeType
  themeMode: ThemeMode
  font: FontOption
  switchThemeMode: (themeMode: ThemeMode) => void
  switchFont: (font: FontOption) => void
}

const THEME_STORAGE_KEY = 'theme'
const FONT_STORAGE_KEY = 'font'

const getSystemTheme = (): ThemeType => {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

const applyTheme = (selectedTheme: ThemeMode): ThemeType => {
  const resolvedTheme = selectedTheme === 'system' ? getSystemTheme() : selectedTheme

  if (typeof document !== 'undefined') {
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(resolvedTheme)
    root.style.colorScheme = resolvedTheme === 'dark' ? 'dark' : 'light'
    generateCSSVariables(resolvedTheme)
  }

  return resolvedTheme
}

const applyFont = (font: FontOption) => {
  if (typeof document === 'undefined') return
  const fontValue =
    font === 'system'
      ? 'var(--font-family-system)'
      : 'var(--font-family-song)'

  document.documentElement.style.setProperty('--font-family-base', fontValue)
}

const getInitialThemeMode = (): ThemeMode => {
  if (typeof localStorage === 'undefined') return 'system'
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system'
}

const getInitialFont = (): FontOption => {
  if (typeof localStorage === 'undefined') return 'song'
  const stored = localStorage.getItem(FONT_STORAGE_KEY)
  return stored === 'system' || stored === 'song' ? stored : 'song'
}

const initialThemeMode = getInitialThemeMode()
const initialFont = getInitialFont()
const initialTheme = applyTheme(initialThemeMode)
applyFont(initialFont)

const useThemeStore = create<ThemeState>((set) => ({
  theme: initialTheme,
  themeMode: initialThemeMode,
  font: initialFont,
  switchThemeMode: (themeMode) => {
    const appliedTheme = applyTheme(themeMode)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode)
    }
    set({ themeMode, theme: appliedTheme })
  },
  switchFont: (font) => {
    applyFont(font)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(FONT_STORAGE_KEY, font)
    }
    set({ font })
  },
}))

// 监听系统主题变化
let mediaQuery: MediaQueryList | null = null
const handleSystemThemeChange = () => {
  const { themeMode } = useThemeStore.getState()
  if (themeMode === 'system') {
    const appliedTheme = applyTheme('system')
    useThemeStore.setState({ theme: appliedTheme })
  }
}

if (typeof window !== 'undefined') {
  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  mediaQuery.addEventListener('change', handleSystemThemeChange)
}

export const cleanupThemeStore = () => {
  if (mediaQuery) {
    mediaQuery.removeEventListener('change', handleSystemThemeChange)
    mediaQuery = null
  }
}

export default useThemeStore
